import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { useCreateComment } from '../comments/hooks';
import { useMarkRoomRead, useRoomUnread } from '../messages/hooks';
import { roomUnreadBadgeText } from '../messages/rooms';
import { ConsoleConversation } from '../huddlePortal/ConsoleConversation';
import {
  consoleVoiceAccess,
  loadConsoleOwnerMessages,
  searchConsoleMessages,
} from '../huddlePortal/huddleApi';
import { readConsoleReactions } from '../huddlePortal/consoleReactions';
import { addReaction, removeReaction } from '../comments/reactions';
const PANEL_STORAGE_KEY = 'tap-console-chat-open';
function useNarrowConsole() {
  const [narrow, setNarrow] = useState(
    () => window.matchMedia?.('(max-width: 1099px)').matches ?? false,
  );
  useEffect(() => {
    const media = window.matchMedia?.('(max-width: 1099px)');
    if (media === undefined) return;
    const update = () => setNarrow(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return narrow;
}

export function ConsoleChatPanel() {
  const { client, access } = useAuth();
  const callerId = access.status === 'approved' ? access.profile.id : undefined;
  const queryClient = useQueryClient();
  const narrow = useNarrowConsole();
  const [sheetOpen, setSheetOpen] = useState(
    () => !narrow && localStorage.getItem(PANEL_STORAGE_KEY) !== 'closed',
  );
  useEffect(() => {
    if (narrow) setSheetOpen(false);
  }, [narrow]);
  const room = useQuery({
    queryKey: ['console-dm-room', callerId],
    queryFn: async () => {
      const { data, error } = await client.rpc('console_dm_room');
      if (error) throw Error('Chat unavailable');
      return data ?? '';
    },
    enabled: callerId !== undefined,
    retry: false,
    refetchInterval: sheetOpen ? 15000 : false,
  });
  const roomId = room.data ?? '';
  const createComment = useCreateComment(null, roomId);
  const unread = useRoomUnread();
  const sendError = createComment.error;
  useEffect(() => {
    if (sendError) console.error('console-chat-send', sendError);
  }, [sendError]);
  const markRead = useMarkRoomRead();
  const badge = roomUnreadBadgeText(roomId === '' ? 0 : unread.data?.get(roomId));
  function setOpen(open: boolean) {
    setSheetOpen(open);
    localStorage.setItem(PANEL_STORAGE_KEY, open ? 'open' : 'closed');
  }
  return (
    <>
      <aside
        className={`my-day-console-chat${narrow ? ' my-day-console-chat--sheet' : ''}${sheetOpen ? '' : ' my-day-console-chat--closed'}`}
        aria-label="Chat with Alex"
      >
        {roomId !== '' ? (
          <ConsoleConversation
            key={`${callerId}:${roomId}`}
            open={sheetOpen}
            voiceAccess={consoleVoiceAccess(client)}
            title="Chat with Alex"
            closeLabel="Close chat"
            phone={false}
            badge={badge}
            onClose={() => setOpen(false)}
            read={(cursor) => loadConsoleOwnerMessages(client, cursor)}
            search={(query, cursor) => searchConsoleMessages(client, query, null, cursor)}
            markRead={() => markRead.mutate(roomId)}
            reactions={{
              partner: 'Alex',
              read: (ids) => readConsoleReactions(client, ids, null),
              // Kyu reacts as himself: the table's own RLS decides.
              set: (id, emoji, on) =>
                on
                  ? addReaction(client, id, emoji)
                  : removeReaction(client, id, emoji, callerId ?? ''),
            }}
            send={async (body, source) => {
              await createComment.mutateAsync({
                body,
                mentionedUserIds: [],
                quotedCommentId: source,
              });
              void queryClient.invalidateQueries({ queryKey: ['rooms'] });
              void queryClient.invalidateQueries({
                queryKey: ['comments', 'thread', `project:${roomId}`],
              });
            }}
          />
        ) : (
          <>
            <header className="my-day-console-chat-head">
              <h2>Chat with Alex</h2>
              <button type="button" aria-label="Close chat" onClick={() => setOpen(false)}>
                ×
              </button>
            </header>
            <p className="my-day-console-chat-status">
              {room.isPending ? 'Loading chat…' : 'Your chat with Alex is not available yet.'}
            </p>
          </>
        )}
        {sendError ? (
          <p className="my-day-console-chat-reason">
            {[
              sendError.message,
              ...['code', 'status', 'details', 'hint'].flatMap((key) => {
                const value = (sendError as unknown as Record<string, unknown>)[key];
                return typeof value === 'string' || typeof value === 'number'
                  ? [`${key}: ${value}`]
                  : [];
              }),
            ]
              .join(' | ')
              .slice(0, 200)}
          </p>
        ) : null}
      </aside>
      <button
        type="button"
        className={`my-day-console-chat-trigger${sheetOpen ? ' my-day-console-chat-trigger--hidden' : ''}`}
        aria-expanded={sheetOpen}
        onClick={() => setOpen(true)}
      >
        Chat with Alex{badge === null ? '' : ` (${badge})`}
      </button>
    </>
  );
}
